import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import "@mantine/dates/styles.css";
import API from "utils/helpers/APIHelper";
import { ENDPOINTS } from "API/apiConstants";
import RoomBookingCard from "./RoomBookingCard";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { currencySymbol } from "shared/CurrencySymbol";
import SEO from "components/SEO/SEO";

interface NamedItem {
  id: number;
  name: string;
}

interface RoomData {
  id: number;
  name: string;
  max_guests?: number | null;
  price_per_month: string;
  booked_dates: [string, string][];
  status: string;
  availability_start?: string | null;
  availability_end?: string | null;
  description?: string | null;
  size?: number | null;
  bed_type?: NamedItem | null;
  room_type?: NamedItem | null;
  amenities: NamedItem[];
  features: NamedItem[];
  room_images: { id: number; url: string }[];
  to_book: boolean;
  next_available_date?: string | null;
}

interface PropertyData {
  id: number;
  slug: string;
  title: string;
  city?: string | null;
  neighborhood?: string | null;
  cover_image?: { id: number; url: string } | null;
  is_shared_property: boolean;
  instant_booking: boolean;
  minimum_months_of_booking?: number | null;
  service_fee: number;
  total_bedrooms?: number | null;
  total_bathrooms?: string | number | null;
  availability_start?: string | null;
  availability_end?: string | null;
}

interface OtherRoom {
  id: number;
  name: string;
  public_token: string;
  price_per_month: string;
  thumbnail?: string | null;
  to_book: boolean;
  next_available_date?: string | null;
}

interface RoomLinkResponse {
  room: RoomData;
  property: PropertyData;
  other_rooms?: OtherRoom[];
  prefill: { check_in: string | null; check_out: string | null };
}

const DATE_FORMAT = "YYYY-MM-DD";

type Tone = "good" | "soon" | "none";

const BADGE_CLASSES: Record<Tone, string> = {
  good: "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  soon: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200",
  none: "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300",
};

const TEXT_CLASSES: Record<Tone, string> = {
  good: "text-green-600 dark:text-green-400",
  soon: "text-amber-600 dark:text-amber-400",
  none: "text-neutral-500",
};

const availabilityFromDate = (
  value?: string | null
): { text: string; tone: Tone } => {
  if (value === "unavailable") return { text: "Not available", tone: "none" };
  if (!value) return { text: "Available", tone: "good" };
  const parsed = moment(value, [DATE_FORMAT, "MMM DD,YYYY"], true);
  if (!parsed.isValid()) return { text: "Available", tone: "good" };
  if (parsed.isSameOrBefore(moment(), "day")) {
    return { text: "Available now", tone: "good" };
  }
  return {
    text: `Available from ${parsed.format("MMM D, YYYY")}`,
    tone: "soon",
  };
};

const roomAvailability = (
  nextDate: string | null | undefined,
  toBook: boolean,
  hasDates: boolean
): { text: string; tone: Tone } => {
  if (hasDates) {
    return toBook
      ? { text: "Available for your dates", tone: "good" }
      : { text: "Not available for these dates", tone: "none" };
  }
  return availabilityFromDate(nextDate);
};

const Lightbox: FC<{
  images: string[];
  startIndex: number;
  onClose: () => void;
}> = ({ images, startIndex, onClose }) => {
  const [index, setIndex] = useState(startIndex);
  const next = useCallback(
    () => setIndex((i) => (i + 1) % images.length),
    [images.length]
  );
  const prev = useCallback(
    () => setIndex((i) => (i - 1 + images.length) % images.length),
    [images.length]
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [next, prev, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close photos"
        onClick={onClose}
        className="absolute top-4 right-4 text-white text-3xl leading-none px-3 py-1"
      >
        &times;
      </button>
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Previous photo"
          onClick={(event) => {
            event.stopPropagation();
            prev();
          }}
          className="absolute left-3 sm:left-6 text-white text-4xl leading-none px-3 py-2"
        >
          &lsaquo;
        </button>
      )}
      <img
        src={images[index]}
        alt=""
        className="max-h-[85vh] max-w-[92vw] object-contain"
        onClick={(event) => event.stopPropagation()}
      />
      {images.length > 1 && (
        <button
          type="button"
          aria-label="Next photo"
          onClick={(event) => {
            event.stopPropagation();
            next();
          }}
          className="absolute right-3 sm:right-6 text-white text-4xl leading-none px-3 py-2"
        >
          &rsaquo;
        </button>
      )}
      <div className="absolute bottom-4 text-white text-sm">
        {index + 1} / {images.length}
      </div>
    </div>
  );
};

const mosaicClass = (index: number, count: number): string => {
  if (index === 0) {
    if (count === 1) return "md:col-span-4 md:row-span-2";
    return count === 2
      ? "md:col-span-2 md:row-span-2"
      : "md:col-span-2 md:row-span-2";
  }
  if (count === 2) return "hidden md:block md:col-span-2 md:row-span-2";
  if (count === 3) return "hidden md:block md:col-span-2";
  if (count === 4 && index === 1) return "hidden md:block md:col-span-2";
  return "hidden md:block";
};

const RoomBookingPage: FC = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const inParam = searchParams.get("in");
  const outParam = searchParams.get("out");

  const [data, setData] = useState<RoomLinkResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  const [checkIn, setCheckIn] = useState<string | null>(null);
  const [checkOut, setCheckOut] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotAvailable(false);
      window.scrollTo(0, 0);
      try {
        const query = new URLSearchParams();
        if (inParam) query.set("in", inParam);
        if (outParam) query.set("out", outParam);
        const response = await API.get(
          ENDPOINTS.GET_ROOM_LINK(token || "", query.toString())
        );
        if (cancelled) return;
        // API.get may return the body directly or an axios-style { data } object.
        const raw: any = response;
        const body: RoomLinkResponse | undefined = raw?.room ? raw : raw?.data;
        if (!body?.room) throw new Error("Unexpected room link response");
        setData(body);
        setCheckIn(body.prefill?.check_in ?? null);
        setCheckOut(body.prefill?.check_out ?? null);
      } catch (error) {
        if (!cancelled) setNotAvailable(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, inParam, outParam]);

  const bookedRanges = useMemo(
    () => (data?.room.booked_dates || []).filter(([s, e]) => s && e),
    [data]
  );

  if (loading) {
    return (
      <div className="container max-w-6xl mx-auto py-16 px-4 text-neutral-500">
        Loading room...
      </div>
    );
  }

  if (notAvailable || !data) {
    return (
      <div className="container max-w-xl mx-auto py-20 px-4 text-center">
        <h1 className="text-2xl font-semibold mb-3">This link isn't available</h1>
        <p className="text-neutral-500 mb-6">
          The room may have been booked or removed. You can browse other rooms
          instead.
        </p>
        <Link to="/listing-stay-map" className="text-orange-500 underline">
          Browse rooms
        </Link>
      </div>
    );
  }

  const { room, property } = data;
  const otherRooms = data.other_rooms || [];
  const hasUrlDates = Boolean(data.prefill?.check_in && data.prefill?.check_out);

  const rent = Number(room.price_per_month) || 0;
  const serviceFee = (rent * (property.service_fee || 0)) / 100;
  const total = rent + serviceFee;
  const months =
    checkIn && checkOut
      ? Math.max(
          0,
          Math.ceil(moment(checkOut).diff(moment(checkIn), "months", true))
        )
      : 0;
  const selectionConflict = Boolean(
    checkIn &&
      checkOut &&
      bookedRanges.some(
        ([start, end]) =>
          moment(start).isBefore(checkOut) && moment(end).isAfter(checkIn)
      )
  );
  const money = (amount: number) => `${currencySymbol()} ${amount.toFixed(2)}`;

  const location = [property.neighborhood, property.city]
    .filter(Boolean)
    .join(", ");
  const houseMeta = [
    location,
    property.is_shared_property ? "Shared home" : null,
    property.total_bedrooms ? `${property.total_bedrooms} bedrooms` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const roomImages = room.room_images.map((image) => image.url);
  const fallbackImage = property.cover_image?.url;
  const gallery =
    roomImages.length > 0 ? roomImages : fallbackImage ? [fallbackImage] : [];
  const mosaic = gallery.slice(0, 5);
  const details = [...room.features, ...room.amenities];
  const headerBadge = roomAvailability(
    room.next_available_date,
    room.to_book,
    hasUrlDates
  );
  const datesQuery = checkIn && checkOut ? `?in=${checkIn}&out=${checkOut}` : "";
  const houseLink = `/h/${property.slug}${datesQuery}`;

  const handleShare = async () => {
    const url = `${window.location.origin}/r/${token}${datesQuery}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      window.prompt("Copy this link", url);
    }
  };

  const scrollToBooking = () => {
    document
      .getElementById("booking-card")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="nc-RoomBookingPage container max-w-6xl mx-auto pt-6 pb-28 lg:pb-10 px-4">

         <SEO
        title={`${room.name} · ${property.title}`}
        description={`${room.name} at ${property.title}. Check availability and send a booking request.`}
        canonical={`/h/${property.slug}`}
        noIndex={true}
      />
      <Link
        to={houseLink}
        className="text-sm text-neutral-500 hover:text-orange-500"
      >
        <i className="las la-arrow-left"></i> {property.title}
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mt-3 mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold">{room.name}</h1>
          {location && (
            <p className="text-sm text-neutral-500 mt-1">
              <i className="las la-map-marker-alt text-lg"></i> {location}
              {property.is_shared_property ? " · Shared home" : ""}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleShare}
          className="text-sm rounded-full border border-neutral-300 dark:border-neutral-600 px-4 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800"
        >
          <i className="las la-share-alt"></i> {copied ? "Link copied" : "Share"}
        </button>
      </div>

      {/* PHOTOS */}
      <div className="relative mb-8">
        <div className="grid grid-cols-1 grid-rows-1 md:grid-cols-4 md:grid-rows-2 gap-2 rounded-2xl overflow-hidden h-64 sm:h-80 md:h-[26rem]">
          {mosaic.length === 0 && (
            <div className="md:col-span-4 md:row-span-2 bg-neutral-100 dark:bg-neutral-800" />
          )}
          {mosaic.map((src, index) => (
            <button
              key={`${src}-${index}`}
              type="button"
              onClick={() => setLightboxIndex(index)}
              className={`relative overflow-hidden bg-neutral-100 dark:bg-neutral-800 ${mosaicClass(
                index,
                mosaic.length
              )}`}
            >
              <img
                src={src}
                alt={`${room.name} photo ${index + 1}`}
                className="absolute inset-0 w-full h-full object-cover hover:opacity-90 transition-opacity"
              />
            </button>
          ))}
        </div>
        {gallery.length > 1 && (
          <button
            type="button"
            onClick={() => setLightboxIndex(0)}
            className="absolute bottom-3 right-3 text-sm bg-white text-neutral-900 rounded-lg px-3 py-1.5 shadow"
          >
            Show all {gallery.length} photos
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* LEFT COLUMN */}
        <div className="lg:col-span-2">
          <div className="flex flex-wrap gap-2 pb-6">
            {room.room_type?.name && (
              <span className="text-sm rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                <i className="las la-door-open"></i> {room.room_type.name}
              </span>
            )}
            {room.bed_type?.name && (
              <span className="text-sm rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                <i className="las la-bed"></i> {room.bed_type.name}
              </span>
            )}
            {room.max_guests ? (
              <span className="text-sm rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                <i className="las la-user"></i> {room.max_guests}{" "}
                {room.max_guests === 1 ? "guest" : "guests"}
              </span>
            ) : null}
            {room.size ? (
              <span className="text-sm rounded-lg bg-neutral-100 dark:bg-neutral-800 px-3 py-1.5">
                <i className="las la-expand-arrows-alt"></i> {room.size} sq ft
              </span>
            ) : null}
            <span
              className={`text-sm rounded-lg px-3 py-1.5 ${
                BADGE_CLASSES[headerBadge.tone]
              }`}
            >
              {headerBadge.text}
            </span>
          </div>

          {room.description && (
            <section className="border-t border-neutral-200 dark:border-neutral-700 py-6">
              <h2 className="text-xl font-semibold mb-3">About this room</h2>
              <p className="text-neutral-600 dark:text-neutral-300 break-words font-light leading-relaxed">
                {room.description}
              </p>
            </section>
          )}

          {details.length > 0 && (
            <section className="border-t border-neutral-200 dark:border-neutral-700 py-6">
              <h2 className="text-xl font-semibold mb-3">In the room</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                {details.map((item) => (
                  <div
                    key={`${item.id}-${item.name}`}
                    className="text-sm text-neutral-700 dark:text-neutral-300"
                  >
                    <i className="las la-check text-green-600 mr-2"></i>
                    {item.name.trim()}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="border-t border-neutral-200 dark:border-neutral-700 py-6">
            <h2 className="text-xl font-semibold mb-3">The house</h2>
            <div className="flex items-center gap-4 rounded-2xl border border-neutral-200 dark:border-neutral-700 p-3">
              <div className="w-24 h-20 sm:w-32 sm:h-24 rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 flex-none">
                {fallbackImage && (
                  <img
                    src={fallbackImage}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium line-clamp-2">{property.title}</p>
                {houseMeta && (
                  <p className="text-sm text-neutral-500 mt-1">{houseMeta}</p>
                )}
              </div>
              <Link
                to={houseLink}
                className="flex-none text-sm rounded-full border border-neutral-300 dark:border-neutral-600 px-4 py-2 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                View house
              </Link>
            </div>
          </section>

          {otherRooms.length > 0 && (
            <section className="border-t border-neutral-200 dark:border-neutral-700 py-6">
              <h2 className="text-xl font-semibold mb-3">
                Other rooms in this house
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {otherRooms.map((other) => {
                  const info = roomAvailability(
                    other.next_available_date,
                    other.to_book,
                    hasUrlDates
                  );
                  return (
                    <Link
                      key={other.id}
                      to={`/r/${other.public_token}${datesQuery}`}
                      className="rounded-2xl border border-neutral-200 dark:border-neutral-700 p-2 hover:shadow-md transition-shadow"
                    >
                      <div className="h-24 rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800">
                        {other.thumbnail && (
                          <img
                            src={other.thumbnail}
                            alt={other.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <p className="font-medium text-sm mt-2 truncate">
                        {other.name}
                      </p>
                      <p className="text-sm text-neutral-500">
                        {money(Number(other.price_per_month) || 0)}/mo
                      </p>
                      <p className={`text-xs mt-1 ${TEXT_CLASSES[info.tone]}`}>
                        {info.text}
                      </p>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}
        </div>

        {/* BOOKING CARD */}
        <div className="lg:col-span-1">
          <RoomBookingCard
            room={room}
            property={property}
            checkIn={checkIn}
            checkOut={checkOut}
            onDatesChange={(nextIn, nextOut) => {
              setCheckIn(nextIn);
              setCheckOut(nextOut);
            }}
          />
        </div>
      </div>

      {/* MOBILE STICKY BAR */}
      <div className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700 px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold leading-tight">
            {money(rent)}
            <span className="text-xs text-neutral-500 font-normal">
              {" "}
              / month
            </span>
          </div>
          {months > 0 && !selectionConflict && (
            <div className="text-xs text-neutral-500">
              {months} {months === 1 ? "month" : "months"} · {money(total)}{" "}
              total
            </div>
          )}
        </div>
        <ButtonPrimary onClick={scrollToBooking}>Book room</ButtonPrimary>
      </div>

      {lightboxIndex !== null && gallery.length > 0 && (
        <Lightbox
          images={gallery}
          startIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}
    </div>
  );
};

export default RoomBookingPage;